import { createContext, useContext } from 'react';
import type { MeResponse, PublicUser } from '../../shared/protocol.ts';

export interface AuthContextValue {
  /** null until /api/me has answered. */
  me: MeResponse | null;
  user: PublicUser | null;
  /** True when a signed-in player still has to pick a username. */
  needsUsername: boolean;
  canSignIn: boolean;
  refresh: () => Promise<void>;
  setUser: (user: PublicUser) => void;
  signIn: (returnTo?: string) => void;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export async function fetchMe(): Promise<MeResponse> {
  const res = await fetch('/api/me', { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`me failed: ${res.status}`);
  return (await res.json()) as MeResponse;
}

export async function suggestUsername(locale: string): Promise<string | null> {
  const res = await fetch(`/api/me/username/suggest?locale=${encodeURIComponent(locale)}`);
  if (!res.ok) return null;
  return ((await res.json()) as { username: string }).username;
}

export async function checkUsername(name: string): Promise<{ available: boolean; problem?: string }> {
  const res = await fetch(`/api/me/username/check?name=${encodeURIComponent(name)}`);
  return (await res.json()) as { available: boolean; problem?: string };
}

export type SaveUsernameResult = { ok: true; user: PublicUser } | { ok: false; error: string };

/** Change an existing username; costs RENAME_PRICE gold. */
export async function changeUsername(username: string): Promise<SaveUsernameResult> {
  try {
    const res = await fetch('/api/me/username/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    const body = (await res.json()) as { user?: PublicUser; error?: string };
    return res.ok && body.user ? { ok: true, user: body.user } : { ok: false, error: body.error ?? 'unavailable' };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export async function saveUsername(username: string): Promise<SaveUsernameResult> {
  try {
    const res = await fetch('/api/me/username', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    const body = (await res.json()) as { user?: PublicUser; error?: string };
    return res.ok && body.user ? { ok: true, user: body.user } : { ok: false, error: body.error ?? 'unavailable' };
  } catch {
    return { ok: false, error: 'network' };
  }
}
